import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import urljoin from 'url-join';

import { withConfig } from 'Context/ConfigContext';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';
import fetch2, { METHOD } from 'Util/fetch';
import { Flex, SvgIcon, Text } from 'Components/Base';
import CourseInfoPanel from 'Components/Classroom/CourseInfoPanel/';
import { ClassroomDrawer } from 'Components/Classroom/Components';
import { useCapacitor } from 'Util/Capacitors';
import { useRestorePlayerFocus } from 'Util/Hooks';

import { getDuration } from '../../../Util/datetime';
import { ClassroomNavigation, ClassroomInformation } from '../../../Domain/Classroom';
import { PROVIDER_NAME, OUTSIDE_LINK_URL } from '../../../Util/constants';

import IconComponent from './IconComponent';

const Header = ({
  isDrawerOpen,
  onExitClassroom,
  onToggleDrawer,
  horizontalDrawer,
  isClassroomActive,
  appConfig,
  ...props
}) => {
  const { openUrl } = useCapacitor();
  const restorePlayerFocus = useRestorePlayerFocus();
  const handleDrawerClose = () => {
    onToggleDrawer(false);
    restorePlayerFocus();
  };
  const { userApi, classroom: classroomConfig } = appConfig;
  const dashboardURL = classroomConfig?.clearByOnDemand?.dashboardURL;

  const { data } = useMyExternalProfile({ fetchPolicy: 'cache-only' });
  const getToken = useCallback(async () => {
    const providerName = data?.myExternalProfile?.providerName;
    return await fetch2(
      urljoin(userApi, `/me/externalToken?providerName=${providerName}`),
      null,
      METHOD.GET,
      null,
      {
        isRedirect: false,
        includeCredentials: true,
      }
    );
  }, [data?.myExternalProfile?.providerName, userApi]);

  const goToClear = async () => {
    const token = await getToken();
    const clearUrlLink = dashboardURL.replace('{token}', token);
    openUrl(clearUrlLink);
  };

  const goToOnlineSolution = () => openUrl(OUTSIDE_LINK_URL.ONLINE_SOLUTION);

  const dataTestIdPrefix = 'course-info';

  return (
    <Flex
      width={'100%'}
      justifyContent={'space-between'}
      alignItems={'center'}
      pt={1}
      pb={3}
      py={3}
      {...props}
    >
      <IconComponent
        data-testid={`btn-${dataTestIdPrefix}-back`}
        iconComponent={<SvgIcon icon={SvgIcon.ICON_LIST.ARROW_LEFT} color={'white'} size={24} />}
        onClick={onExitClassroom}
      />
      <ClassroomNavigation>
        {({ currentSubsection }) => (
          <Text
            textStyle={'heading5'}
            data-testid={`${dataTestIdPrefix}-current-title`}
            color={'white'}
          >
            {currentSubsection && currentSubsection.title}
          </Text>
        )}
      </ClassroomNavigation>
      <ClassroomInformation>
        {({
          title,
          coursePackage,
          totalVideoTime,
          instructor,
          remainingTime,
          expiryDate,
          providerName,
          progressPercent,
          subsectionCount,
        }) => {
          const { isOpenExtLink } = appConfig;
          const showActionPanel = isOpenExtLink && providerName === PROVIDER_NAME.ONDEMAND;
          return (
            <CourseInfoPanel
              coursePackage={coursePackage}
              subsectionCount={subsectionCount}
              totalTime={getDuration(totalVideoTime)}
              instructor={instructor}
              timeLeft={getDuration(remainingTime)}
              expiryDate={expiryDate}
              progress={progressPercent}
              title={title}
              onClickAction1={goToClear}
              onClickAction2={goToOnlineSolution}
              showActionPanel={showActionPanel}
              toggleComponent={
                <IconComponent
                  data-testid={`${dataTestIdPrefix}-panel-toggle`}
                  iconComponent={
                    <SvgIcon icon={SvgIcon.ICON_LIST.INFO_OUTLINED} color={'white'} size={24} />
                  }
                />
              }
              drawerProps={{
                onDrawerOpen: () => onToggleDrawer(true),
                onDrawerClose: handleDrawerClose,
                horizontalAnchor: ClassroomDrawer.ANCHOR_TYPES.RIGHT,
                horizontal: horizontalDrawer,
              }}
            />
          );
        }}
      </ClassroomInformation>
    </Flex>
  );
};

Header.propTypes = {
  isDrawerOpen: PropTypes.bool,
  onExitClassroom: PropTypes.func,
  onToggleDrawer: PropTypes.func,
};

Header.defaultProps = {
  onToggleDrawer: () => {},
};

export default withConfig(Header);
